/**
 * pageScroll — robust page-scroll access for the iOS WKWebView.
 *
 * The app scrolls the DOCUMENT (there is no overflow container — every
 * `overflow-y` in the tree is a modal/sheet). But on iOS WKWebView the plain
 * `window.scrollY` / `window.scrollTo` path is unreliable in two ways the Dex
 * relies on:
 *   1. During a route mount the native scroll-restoration fights an early
 *      `window.scrollTo`, so the detail→dex offset restore silently loses.
 *   2. Some WebView builds report/accept the offset only via
 *      `document.scrollingElement` (or `documentElement`/`body`), not `window`,
 *      so a window-only read (back-to-top visibility) stays 0 while the page IS
 *      scrolled, and a window-only write is a no-op.
 *
 * These helpers read the MAX across every plausible owner and write ALL of
 * them, so back-to-top and the scroll restore work regardless of which owner
 * this particular WebView honours. On desktop/Android (where `window` is the
 * owner) the extra owners read/write 0 harmlessly.
 */

function scrollOwners(): HTMLElement[] {
  if (typeof document === "undefined") return [];
  const els: Array<Element | null> = [
    document.scrollingElement,
    document.documentElement,
    document.body,
  ];
  const seen = new Set<Element>();
  const out: HTMLElement[] = [];
  for (const el of els) {
    if (el instanceof HTMLElement && !seen.has(el)) {
      seen.add(el);
      out.push(el);
    }
  }
  return out;
}

/** Current page scroll offset — the max across window + every document owner. */
export function getPageScrollTop(): number {
  if (typeof window === "undefined") return 0;
  let top = window.scrollY || window.pageYOffset || 0;
  for (const el of scrollOwners()) top = Math.max(top, el.scrollTop || 0);
  return Math.round(top);
}

/**
 * Scroll the page to `y`, writing every owner so whichever one this WebView
 * honours moves. Smooth mode only drives `window` (animating the document
 * owners in parallel would fight it); instant mode sets each owner directly —
 * the reliable path when a mount-time `window.scrollTo` is being clobbered.
 */
export function scrollPageTo(y: number, smooth = false): void {
  if (typeof window === "undefined") return;
  const writeAll = (top: number) => {
    try {
      window.scrollTo(0, top);
    } catch {
      /* ignore */
    }
    for (const el of scrollOwners()) {
      try {
        el.scrollTop = top;
      } catch {
        /* a read-only owner — ignore and rely on the others */
      }
    }
  };
  if (!smooth) {
    writeAll(y);
    return;
  }
  // Native smooth `window.scrollTo({behavior:"smooth"})` silently no-ops on iOS
  // WKWebView (why the back-to-top button did nothing), so animate manually and
  // write every owner each frame. Falls back to an instant write if rAF/perf are
  // unavailable.
  const start = getPageScrollTop();
  const dist = y - start;
  if (Math.abs(dist) < 2 || typeof requestAnimationFrame === "undefined") {
    writeAll(y);
    return;
  }
  const durationMs = Math.min(500, 140 + Math.abs(dist) * 0.35);
  const startedAt = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - startedAt) / durationMs);
    const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
    writeAll(Math.round(start + dist * eased));
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/**
 * Subscribe to page scroll from ANY owner. The window listener covers document
 * scroll; the capture-phase document listener also catches a scrolling child
 * (scroll does not bubble, but capture reaches it), so the callback fires no
 * matter which element the WebView actually scrolls. Also fires on resize.
 */
export function onPageScroll(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const capture = { capture: true, passive: true } as const;
  window.addEventListener("scroll", cb, { passive: true });
  document.addEventListener("scroll", cb, capture);
  window.addEventListener("resize", cb, { passive: true });
  return () => {
    window.removeEventListener("scroll", cb);
    document.removeEventListener("scroll", cb, { capture: true });
    window.removeEventListener("resize", cb);
  };
}
